<script setup lang="ts">

import TheBottomSheet from '@/components/TheBottomSheet.vue'
import { ref } from 'vue'
import Phase1Content from '@/components/snapshot/Phase1Content.vue'
import Phase2Content from '@/components/snapshot/Phase2Content.vue'
import Phase3Content from '@/components/snapshot/Phase3Content.vue'
import ChevronLeftIcon from '@/components/icons/ChevronLeftIcon.vue'
import ChevronRightIcon from '@/components/icons/ChevronRightIcon.vue'

defineProps<{
	show: boolean
}>()

defineEmits<{
	(e: 'hide'): void
}>()

const slides = [
  { component: Phase1Content },
  { component: Phase2Content },
  { component: Phase3Content },
]

const activeIndex = ref(0)

const isDragging = ref(false)
const startX = ref(0)
const dragOffset = ref(0)
const THRESHOLD = 50 // px

function onDragStart(e: MouseEvent | TouchEvent) {
  isDragging.value = true
  startX.value = 'touches' in e ? e.touches[0].clientX : e.clientX
}

function onDragMove(e: MouseEvent | TouchEvent) {
  if (!isDragging.value) return
  const currentX = 'touches' in e ? e.touches[0].clientX : e.clientX
  dragOffset.value = currentX - startX.value
}

function onDragEnd() {
  if (!isDragging.value) return
  if (dragOffset.value < -THRESHOLD) next()
  else if (dragOffset.value > THRESHOLD) prev()
  isDragging.value = false
  dragOffset.value = 0
}

const prev = () => {
  if (activeIndex.value > 0) activeIndex.value--
}
const next = () => {
  if (activeIndex.value < slides.length - 1) activeIndex.value++
}
</script>

<template>
  <TheBottomSheet
    :show="show"
    centered
    @hide="$emit('hide')"
  >
    <div class="flex flex-col w-full h-full">
      <div class="flex-1 overflow-y-auto">
        <div class="overflow-hidden">
          <div
            class="flex transition-transform duration-300 ease-in-out"
            :style="{
              transform: `translateX(calc(-${activeIndex * 100}% + ${dragOffset}px))`
            }"
            @mousedown.prevent="onDragStart"
            @touchstart.passive="onDragStart"
            @mousemove.prevent="onDragMove"
            @touchmove.passive="onDragMove"
            @mouseup="onDragEnd"
            @mouseleave="onDragEnd"
            @touchend="onDragEnd"
          >
            <div
              v-for="(slide, i) in slides"
              :key="i"
              class="flex-shrink-0 w-full"
            >
              <component :is="slide.component" />
            </div>
          </div>
        </div>
      </div>
      <div class="w-full sticky bottom-0 mt-4 flex items-center justify-between py-2 backdrop-blur-[1px]">
        <ChevronLeftIcon
          class="size-6 min-w-[24px] transition-[visibility] duration-200"
          :class="{'pointer-events-none invisible': activeIndex === 0}"
          @click="prev"
        />
        <div class="flex justify-center gap-x-4">
          <button
            v-for="(slide, i) in slides"
            :key="i"
            @click="activeIndex = i"
            class="h-3 w-3 rounded-full"
            :class="i === activeIndex ? 'bg-green-primary' : 'bg-dark-top-layer'"
          />
        </div>
        <ChevronRightIcon
          class="size-6 min-w-[24px] transition-[visibility] duration-200"
          :class="{'pointer-events-none invisible': activeIndex === slides.length - 1}"
          @click="next"
        />
      </div>
    </div>
  </TheBottomSheet>
</template>

<style scoped>

</style>